import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Popover,
  PopoverProps,
  PopoverTrigger,
  PopoverTriggerProps,
  Type,
} from '@spotify-internal/encore-web';

const StyledTypeWithPointer = styled(Type)`
  cursor: pointer;
`;

interface Props {
  id: string;
  overlay: PopoverTriggerProps['overlay'];
  lastBtnRef: React.MutableRefObject<HTMLElement | null>;
  popoverTriggerRef: React.MutableRefObject<HTMLElement | null>;
  popoverProps?: PopoverProps;
}

const useFocusRestrict = (
  showPopover: boolean | undefined,
  lastBtnRef: React.MutableRefObject<HTMLElement | null>,
  popoverTriggerRef: React.MutableRefObject<HTMLElement | null>,
) => {
  const firstBtnRef = useRef<HTMLElement | null>(null);
  const [triggered, setTriggered] = useState(false);

  /**
   * focus on first button immediately after showing popover & focus on trigger button immediately after closing popover
   * https://encore.spotify.net/web/components/popover?format=figma-figma#accessibility
   */
  useEffect(() => {
    if (!showPopover && triggered) {
      popoverTriggerRef.current?.focus();
    } else if (showPopover) {
      firstBtnRef.current?.focus();
    }
  }, [showPopover, triggered, popoverTriggerRef]);

  const trigger = () => {
    setTriggered(true);
  };

  const keyDownHandler = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;

    const firstBtn = firstBtnRef.current;
    const lastBtn = lastBtnRef.current;

    if (!e.shiftKey && document.activeElement === firstBtn) {
      lastBtn?.focus();
      e.preventDefault();
    }

    if (e.shiftKey && document!.activeElement === lastBtn) {
      firstBtn?.focus();
      e.preventDefault();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', keyDownHandler);

    return () => {
      window.removeEventListener('keydown', keyDownHandler);
    };
  });

  return { firstBtnRef, trigger };
};

export const HeldReleasesTableRowPopover: React.FC<Props> = ({
  id,
  children,
  overlay,
  lastBtnRef,
  popoverTriggerRef,
  popoverProps,
}) => {
  const [showFlag, setShowFlag] = useState(false);
  const { firstBtnRef, trigger } = useFocusRestrict(
    showFlag,
    lastBtnRef,
    popoverTriggerRef,
  );

  return (
    <PopoverTrigger
      overlay={
        showFlag && (
          <Popover
            id={id}
            data-testid={id}
            closeButtonRef={firstBtnRef}
            {...popoverProps}
          >
            {overlay}
          </Popover>
        )
      }
      placement={PopoverTrigger.bottom}
      onShow={() => {
        setShowFlag(true);
        trigger();
      }}
      onHide={() => {
        setShowFlag(false);
        trigger();
      }}
    >
      <StyledTypeWithPointer variant="mesto">{children}</StyledTypeWithPointer>
    </PopoverTrigger>
  );
};
